import { translate } from '../../i18n/index.js';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { MembershipTier } from '@atomfit/shared';
import { AdminLayout } from '../../components/layout/AdminLayout.js';
import { CustomSelect, Spinner, Tag, formatCurrency } from '../../components/design/AtomFitUI.js';
import { adminApi, type PlatformMembershipPlan } from '../../api/admin.api.js';
import { useAuth } from '../../hooks/useAuth.js';

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; plan: PlatformMembershipPlan };

interface PlanForm {
  name: string;
  description: string;
  tier: MembershipTier;
  price: string;
  marketingWeeklyPrice: string;
  creditAmount: string;
  slotPrice: string;
  discountPercent: string;
  slotsPerMonth: string;
  guestPassesPerMonth: string;
  maxReservationMinutes: string;
  bookingAdvanceDays: string;
  currency: string;
  sortOrder: string;
  isActive: boolean;
}

const emptyForm: PlanForm = {
  name: '',
  description: '',
  tier: 'BASIC',
  price: '',
  marketingWeeklyPrice: '',
  creditAmount: '',
  slotPrice: '',
  discountPercent: '0',
  slotsPerMonth: '',
  guestPassesPerMonth: '0',
  maxReservationMinutes: '90',
  bookingAdvanceDays: '31',
  currency: 'CZK',
  sortOrder: '10',
  isActive: true,
};

const tierLabels: Record<MembershipTier, string> = {
  BASIC: translate('pages.admin.admin_memberships_page.tier_labels.basic'),
  PREMIUM: translate('pages.admin.admin_memberships_page.tier_labels.premium'),
  ELITE: translate('pages.admin.admin_memberships_page.tier_labels.elite'),
};

function toDisplayAmount(amountInSmallestUnit: number | null | undefined) {
  if (amountInSmallestUnit === null || amountInSmallestUnit === undefined) return '';
  return String(amountInSmallestUnit / 100);
}

function toSmallestUnit(value: string) {
  const normalized = Number(value.replace(',', '.'));
  if (!Number.isFinite(normalized) || normalized < 0) return 0;
  return Math.round(normalized * 100);
}

function nullableInt(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? Math.round(parsed) : null;
}

function planToForm(plan: PlatformMembershipPlan): PlanForm {
  return {
    name: plan.name,
    description: plan.description ?? '',
    tier: plan.tier,
    price: toDisplayAmount(plan.priceInSmallestUnit),
    marketingWeeklyPrice: toDisplayAmount(plan.marketingWeeklyPriceInSmallestUnit),
    creditAmount: toDisplayAmount(plan.creditAmountInSmallestUnit),
    slotPrice: toDisplayAmount(plan.slotPriceInSmallestUnit),
    discountPercent: String(plan.discountPercent),
    slotsPerMonth: plan.slotsPerMonth ? String(plan.slotsPerMonth) : '',
    guestPassesPerMonth: String(plan.guestPassesPerMonth),
    maxReservationMinutes: String(plan.maxReservationMinutes),
    bookingAdvanceDays: String(plan.bookingAdvanceDays),
    currency: plan.currency,
    sortOrder: String(plan.sortOrder),
    isActive: plan.isActive,
  };
}

export function AdminMembershipsPage() {
  const { role } = useAuth();
  const isPlatform = role === 'PLATFORM_ADMIN';
  const [plans, setPlans] = useState<PlatformMembershipPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const [form, setForm] = useState<PlanForm>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  async function load() {
    if (!isPlatform) return;
    setLoading(true);
    setLoadError(null);
    try {
      const planResponse = await adminApi.listMembershipPlans();
      setPlans(planResponse.data.data);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      setLoadError(
        message ??
          translate('pages.admin.admin_memberships_page.load.set_load_error.error_membership'),
      );
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [isPlatform]);

  function openCreate() {
    setForm(emptyForm);
    setError(null);
    setModal({ type: 'create' });
  }

  function openEdit(plan: PlatformMembershipPlan) {
    setForm(planToForm(plan));
    setError(null);
    setModal({ type: 'edit', plan });
  }

  async function savePlan(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        tier: form.tier,
        priceInSmallestUnit: toSmallestUnit(form.price),
        marketingWeeklyPriceInSmallestUnit: toSmallestUnit(form.marketingWeeklyPrice),
        creditAmountInSmallestUnit: toSmallestUnit(form.creditAmount),
        slotPriceInSmallestUnit: form.slotPrice.trim() ? toSmallestUnit(form.slotPrice) : null,
        discountPercent: Number(form.discountPercent),
        slotsPerMonth: nullableInt(form.slotsPerMonth),
        guestPassesPerMonth: Number(form.guestPassesPerMonth || 0),
        maxReservationMinutes: Number(form.maxReservationMinutes),
        bookingAdvanceDays: Number(form.bookingAdvanceDays),
        currency: form.currency.trim().toUpperCase(),
        sortOrder: Number(form.sortOrder || 0),
        isActive: form.isActive,
      };

      if (modal.type === 'create') {
        await adminApi.createMembershipPlan(payload);
      } else if (modal.type === 'edit') {
        await adminApi.updateMembershipPlan(modal.plan.tier, payload);
      }
      setModal({ type: 'none' });
      await load();
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      setError(
        message ??
          translate('pages.admin.admin_memberships_page.save_plan.set_error.error_membership_save'),
      );
    } finally {
      setSaving(false);
    }
  }

  async function togglePlan(plan: PlatformMembershipPlan) {
    await adminApi.updateMembershipPlan(plan.tier, { isActive: !plan.isActive });
    await load();
  }

  return (
    <AdminLayout
      title={translate(
        'pages.admin.admin_memberships_page.admin_memberships_page.admin_layout.membership',
      )}
      eyebrow={translate(
        'pages.admin.admin_memberships_page.admin_memberships_page.admin_layout.eyebrow',
      )}
    >
      {!isPlatform ? (
        <div className="af-card" style={{ padding: 24, color: 'var(--af-muted)' }}>
          {translate(
            'pages.admin.admin_memberships_page.admin_memberships_page.div_card.membership',
          )}
        </div>
      ) : (
        <>
          <div className="af-card" style={{ padding: 16, marginBottom: 16 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 900 }}>
                  {translate('pages.admin.admin_memberships_page.admin_memberships_page.div.copy')}
                </div>
                <div style={{ color: 'var(--af-muted)', fontSize: 12, marginTop: 3 }}>
                  {translate(
                    'pages.admin.admin_memberships_page.admin_memberships_page.div.membership_tenant_save',
                  )}
                </div>
              </div>
              <button
                className="af-primary-btn"
                type="button"
                onClick={openCreate}
                style={{ padding: '12px 18px' }}
              >
                {translate(
                  'pages.admin.admin_memberships_page.admin_memberships_page.button_primary_btn.add',
                )}
              </button>
            </div>
          </div>

          <div className="af-card" style={{ overflow: 'auto' }}>
            {loading ? (
              <div
                style={{
                  padding: 24,
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  color: 'var(--af-muted)',
                }}
              >
                <Spinner />{' '}
                {translate(
                  'pages.admin.admin_memberships_page.admin_memberships_page.div.loading_membership',
                )}
              </div>
            ) : loadError ? (
              <div style={{ padding: 24 }}>
                <div className="af-error" style={{ marginBottom: 14 }}>
                  {loadError}
                </div>
                <button
                  className="af-outline-btn"
                  type="button"
                  onClick={() => void load()}
                  style={{ padding: '10px 14px' }}
                >
                  {translate(
                    'pages.admin.admin_memberships_page.admin_memberships_page.button_outline_btn.retry',
                  )}
                </button>
              </div>
            ) : plans.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--af-muted)' }}>
                {translate(
                  'pages.admin.admin_memberships_page.admin_memberships_page.div.membership',
                )}
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--af-border)' }}>
                    {[
                      translate(
                        'pages.admin.admin_memberships_page.admin_memberships_page.tr.copy',
                      ),
                      translate(
                        'pages.admin.admin_memberships_page.admin_memberships_page.tr.tenant',
                      ),
                      translate(
                        'pages.admin.admin_memberships_page.admin_memberships_page.tr.price',
                      ),
                      translate(
                        'pages.admin.admin_memberships_page.admin_memberships_page.tr.credit_edit',
                      ),
                      translate(
                        'pages.admin.admin_memberships_page.admin_memberships_page.tr.slot',
                      ),
                      translate(
                        'pages.admin.admin_memberships_page.admin_memberships_page.tr.copy_2',
                      ),
                      translate(
                        'pages.admin.admin_memberships_page.admin_memberships_page.tr.active',
                      ),

                      '',
                    ].map((heading) => (
                      <th
                        key={heading}
                        style={{
                          padding: '12px 14px',
                          textAlign: 'left',
                          fontSize: 10,
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          color: 'var(--af-dim)',
                        }}
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {plans.map((plan) => (
                    <tr key={plan.id} style={{ borderBottom: '1px solid rgba(124,118,106,0.12)' }}>
                      <td style={{ padding: '13px 14px' }}>
                        <div style={{ fontWeight: 900 }}>{plan.name}</div>
                        <div
                          style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 5 }}
                        >
                          <Tag>{tierLabels[plan.tier]}</Tag>
                          {!plan.isActive && (
                            <Tag color="var(--af-danger)" bg="rgba(232,66,26,0.1)">
                              {translate(
                                'pages.admin.admin_memberships_page.admin_memberships_page.tag.inactive',
                              )}
                            </Tag>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '13px 14px' }}>
                        <div style={{ fontWeight: 900 }}>{plan.franchiseCount ?? 1}</div>
                        <div style={{ color: 'var(--af-dim)', fontSize: 11 }}>
                          {translate(
                            'pages.admin.admin_memberships_page.admin_memberships_page.div.tenant',
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '13px 14px', fontWeight: 900 }}>
                        <div>
                          {formatCurrency(plan.priceInSmallestUnit, plan.currency)}{' '}
                          {translate(
                            'pages.admin.admin_memberships_page.admin_memberships_page.div.time',
                          )}
                        </div>
                        <div style={{ color: 'var(--af-dim)', fontSize: 11, marginTop: 3 }}>
                          {translate(
                            'pages.admin.admin_memberships_page.admin_memberships_page.div.campaign',
                          )}
                          {formatCurrency(plan.marketingWeeklyPriceInSmallestUnit, plan.currency)}{' '}
                          {translate(
                            'pages.admin.admin_memberships_page.admin_memberships_page.div.time_2',
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '13px 14px' }}>
                        {formatCurrency(plan.creditAmountInSmallestUnit, plan.currency)}
                      </td>
                      <td style={{ padding: '13px 14px' }}>
                        {plan.slotPriceInSmallestUnit !== null
                          ? formatCurrency(plan.slotPriceInSmallestUnit, plan.currency)
                          : translate(
                              'pages.admin.admin_memberships_page.admin_memberships_page.td.copy',
                              { value1: plan.discountPercent },
                            )}
                      </td>
                      <td style={{ padding: '13px 14px', color: 'var(--af-muted)' }}>
                        <div>
                          {plan.slotsPerMonth ?? '-'}{' '}
                          {translate(
                            'pages.admin.admin_memberships_page.admin_memberships_page.div.slot',
                          )}
                        </div>
                        <div>
                          {plan.maxReservationMinutes}{' '}
                          {translate(
                            'pages.admin.admin_memberships_page.admin_memberships_page.div.copy_2',
                          )}{' '}
                          {plan.bookingAdvanceDays}{' '}
                          {translate(
                            'pages.admin.admin_memberships_page.admin_memberships_page.div.copy_3',
                          )}
                        </div>
                        <div>
                          {plan.guestPassesPerMonth}{' '}
                          {translate(
                            'pages.admin.admin_memberships_page.admin_memberships_page.div.time_guest',
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '13px 14px' }}>
                        <div style={{ fontWeight: 900 }}>
                          {plan.totalActiveMemberships ?? plan._count.customerMemberships}
                        </div>
                        <div style={{ color: 'var(--af-dim)', fontSize: 11 }}>
                          {plan.totalPurchases ?? plan._count.purchases}{' '}
                          {translate(
                            'pages.admin.admin_memberships_page.admin_memberships_page.div.copy_4',
                          )}
                        </div>
                      </td>
                      <td
                        style={{ padding: '13px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}
                      >
                        <button
                          className="af-outline-btn"
                          type="button"
                          onClick={() => void togglePlan(plan)}
                          style={{ padding: '8px 10px', marginRight: 8 }}
                        >
                          {plan.isActive
                            ? translate(
                                'pages.admin.admin_memberships_page.admin_memberships_page.button_outline_btn.label',
                              )
                            : translate(
                                'pages.admin.admin_memberships_page.admin_memberships_page.button_outline_btn.label_2',
                              )}
                        </button>
                        <button
                          className="af-outline-btn"
                          type="button"
                          onClick={() => openEdit(plan)}
                          style={{ padding: '8px 10px' }}
                        >
                          {translate(
                            'pages.admin.admin_memberships_page.admin_memberships_page.button_outline_btn.edit',
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {modal.type !== 'none' && (
            <div
              className="af-profile-modal-backdrop"
              role="presentation"
              onClick={() => setModal({ type: 'none' })}
            >
              <form
                className="af-profile-modal"
                onSubmit={(event) => void savePlan(event)}
                onClick={(event) => event.stopPropagation()}
                style={{
                  width: 'min(760px, calc(100vw - 32px))',
                  maxHeight: 'calc(100vh - 60px)',
                  overflow: 'auto',
                }}
              >
                <h2 style={{ margin: '0 0 16px', fontSize: 22 }}>
                  {modal.type === 'create'
                    ? translate(
                        'pages.admin.admin_memberships_page.admin_memberships_page.h2.membership',
                      )
                    : translate(
                        'pages.admin.admin_memberships_page.admin_memberships_page.h2.membership_edit',
                      )}
                </h2>
                {error && (
                  <div className="af-error" style={{ marginBottom: 14 }}>
                    {error}
                  </div>
                )}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 12,
                  }}
                >
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_memberships_page.admin_memberships_page.label_label.copy',
                    )}

                    <CustomSelect
                      value={form.tier}
                      onChange={(tier) =>
                        setForm((current) => ({ ...current, tier: tier as MembershipTier }))
                      }
                      options={[
                        {
                          value: 'BASIC',
                          label: translate(
                            'pages.admin.admin_memberships_page.admin_memberships_page.custom_select.options',
                          ),
                        },
                        {
                          value: 'PREMIUM',
                          label: translate(
                            'pages.admin.admin_memberships_page.admin_memberships_page.custom_select.options_2',
                          ),
                        },
                        {
                          value: 'ELITE',
                          label: translate(
                            'pages.admin.admin_memberships_page.admin_memberships_page.custom_select.options_3',
                          ),
                        },
                      ]}
                    />
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_memberships_page.admin_memberships_page.label_label.copy_2',
                    )}

                    <input
                      className="af-input"
                      value={form.name}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, name: event.target.value }))
                      }
                      required
                    />
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_memberships_page.admin_memberships_page.label_label.currency',
                    )}

                    <input
                      className="af-input"
                      value={form.currency}
                      maxLength={3}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          currency: event.target.value.toUpperCase(),
                        }))
                      }
                      required
                    />
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_memberships_page.admin_memberships_page.label_label.price',
                    )}

                    <input
                      className="af-input"
                      inputMode="decimal"
                      value={form.price}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, price: event.target.value }))
                      }
                      required
                    />
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_memberships_page.admin_memberships_page.label_label.campaign',
                    )}

                    <input
                      className="af-input"
                      inputMode="decimal"
                      value={form.marketingWeeklyPrice}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          marketingWeeklyPrice: event.target.value,
                        }))
                      }
                      required
                    />
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_memberships_page.admin_memberships_page.label_label.credit_edit',
                    )}

                    <input
                      className="af-input"
                      inputMode="decimal"
                      value={form.creditAmount}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, creditAmount: event.target.value }))
                      }
                      required
                    />
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_memberships_page.admin_memberships_page.label_label.slot',
                    )}

                    <input
                      className="af-input"
                      inputMode="decimal"
                      value={form.slotPrice}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, slotPrice: event.target.value }))
                      }
                      placeholder={translate(
                        'pages.admin.admin_memberships_page.admin_memberships_page.input_input.placeholder_content',
                      )}
                    />
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_memberships_page.admin_memberships_page.label_label.discount',
                    )}

                    <input
                      className="af-input"
                      type="number"
                      min={0}
                      max={100}
                      value={form.discountPercent}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, discountPercent: event.target.value }))
                      }
                    />
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_memberships_page.admin_memberships_page.label_label.slot_2',
                    )}

                    <input
                      className="af-input"
                      type="number"
                      min={1}
                      value={form.slotsPerMonth}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, slotsPerMonth: event.target.value }))
                      }
                    />
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_memberships_page.admin_memberships_page.label_label.time_guest',
                    )}

                    <input
                      className="af-input"
                      type="number"
                      min={0}
                      value={form.guestPassesPerMonth}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          guestPassesPerMonth: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_memberships_page.admin_memberships_page.label_label.booking',
                    )}

                    <input
                      className="af-input"
                      type="number"
                      min={30}
                      value={form.maxReservationMinutes}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          maxReservationMinutes: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_memberships_page.admin_memberships_page.label_label.calendar',
                    )}

                    <input
                      className="af-input"
                      type="number"
                      min={1}
                      value={form.bookingAdvanceDays}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          bookingAdvanceDays: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_memberships_page.admin_memberships_page.label_label.order',
                    )}

                    <input
                      className="af-input"
                      type="number"
                      min={0}
                      value={form.sortOrder}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, sortOrder: event.target.value }))
                      }
                    />
                  </label>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      color: 'var(--af-muted)',
                      fontSize: 13,
                      fontWeight: 800,
                      paddingTop: 24,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, isActive: event.target.checked }))
                      }
                    />

                    {translate(
                      'pages.admin.admin_memberships_page.admin_memberships_page.label.active',
                    )}
                  </label>
                </div>
                <label className="af-label" style={{ marginTop: 12 }}>
                  {translate(
                    'pages.admin.admin_memberships_page.admin_memberships_page.label_label.copy_3',
                  )}

                  <textarea
                    className="af-input"
                    value={form.description}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, description: event.target.value }))
                    }
                    rows={3}
                  />
                </label>
                <div
                  style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}
                >
                  <button
                    className="af-outline-btn"
                    type="button"
                    onClick={() => setModal({ type: 'none' })}
                  >
                    {translate(
                      'pages.admin.admin_memberships_page.admin_memberships_page.button_outline_btn.cancel',
                    )}
                  </button>
                  <button className="af-primary-btn" type="submit" disabled={saving}>
                    {saving
                      ? translate(
                          'pages.admin.admin_memberships_page.admin_memberships_page.button_primary_btn.label',
                        )
                      : translate(
                          'pages.admin.admin_memberships_page.admin_memberships_page.button_primary_btn.save',
                        )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}
